import { IInputValidation, IValueValidation, InputValidationResultKind } from "client/Validation/input";
import { IValueReducer } from "../reducer";
import { NewValueHandler } from "./misc";

interface IAttachableTextInputProps<TInputValue> {
	readonly value: TInputValue;
	readonly isInvalid: boolean;
	readonly validation: IInputValidation | undefined;
	readonly trackingId: string;
	onChange(newValue: TInputValue): void;
	onBlur(newValue: TInputValue): void;
}

interface IAttachableTextInputPropsWithBuilder<TInputValue> extends IAttachableTextInputProps<TInputValue> {
	withOnChange(handler: (newValue: TInputValue) => void): IAttachableTextInputPropsWithBuilder<TInputValue>;
	withOnBlur(handler: (newValue: TInputValue) => void): IAttachableTextInputPropsWithBuilder<TInputValue>;
}

class AttachableTextInputPropsWithBuilder<TInputValue> implements IAttachableTextInputPropsWithBuilder<TInputValue> {
	private readonly _reducer: IValueReducer<TInputValue>;
	private readonly _extraOnChangeHandlers: NewValueHandler<TInputValue>[];
	private readonly _extraOnBlurHandlers: NewValueHandler<TInputValue>[];

	public readonly value: TInputValue;
	public readonly isInvalid: boolean;
	public readonly validation: IInputValidation;
	public readonly trackingId: string;

	constructor (
		reducer: IValueReducer<TInputValue>,
		validation: IValueValidation<TInputValue>
	) {
		this._reducer = reducer;
		this.validation = validation;
		this._extraOnBlurHandlers = [];
		this._extraOnChangeHandlers = [];

		this.value = this._reducer.value;
		this.isInvalid = this.validation
			? this.validation.getValidationState().kind === InputValidationResultKind.Invalid
			: false;

		this.validation = validation;
		this.trackingId = reducer.path;

		this.onChange = this.onChange.bind(this);
		this.onBlur = this.onBlur.bind(this);
		this.withOnChange = this.withOnChange.bind(this);
		this.withOnBlur = this.withOnBlur.bind(this);
	}

	public withOnChange(handler: (newValue: TInputValue) => void): IAttachableTextInputPropsWithBuilder<TInputValue> {
		this._extraOnChangeHandlers.push(handler);
		return this;
	}

	public withOnBlur(handler: (newValue: TInputValue) => void): IAttachableTextInputPropsWithBuilder<TInputValue> {
		this._extraOnBlurHandlers.push(handler);
		return this;
	}

	public onChange(newValue: TInputValue) {
		this._reducer.setValue(newValue);
		this.validation.triggerValueChanged();

		for (const handler of this._extraOnChangeHandlers) {
			handler(newValue);
		}
	}

	public onBlur(newValue: TInputValue) {
		this.validation.triggerInputFinished();

		for (const handler of this._extraOnBlurHandlers) {
			handler(newValue);
		}
	}
}

export {
	AttachableTextInputPropsWithBuilder,
	type IAttachableTextInputProps,
	type IAttachableTextInputPropsWithBuilder
};